import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const C = {
  text: '#e2e8f0', textMuted: '#94a3b8', textDim: '#64748b',
  purple: '#7c3aed', purpleLight: '#a78bfa', green: '#4ade80', red: '#ef4444',
  blue: '#38bdf8', orange: '#fb923c', yellow: '#facc15',
};

const COLOR_PALETTE = [
  '#7c3aed', '#38bdf8', '#4ade80', '#fb923c', '#f472b6',
  '#facc15', '#ef4444', '#06b6d4', '#a78bfa', '#34d399',
];

const defaultColor = (id) => COLOR_PALETTE[(id - 1) % COLOR_PALETTE.length];

const Loading = () => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh', color: C.textDim }}>
    <div className="spinner" />
    <p style={{ marginTop: 16, fontSize: 13 }}>Loading...</p>
  </div>
);

const ROLE_LEVELS = { admin: 4, director: 3, manager: 2, sales_team: 1, none: 0 };
const canAccess = (user, minRole) => (ROLE_LEVELS[user?.role] || 0) >= (ROLE_LEVELS[minRole] || 0);

export default function AdminPage({ user }) {
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';
  const [tab, setTab] = useState(isAdmin ? 'users' : 'subscriptions');
  const [config, setConfig] = useState([]);
  const [available, setAvailable] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState('sales_team');

  // Subscription state
  const [subTypes, setSubTypes] = useState([]);
  const [newSubName, setNewSubName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [viewingSubId, setViewingSubId] = useState(null);
  const [subMembers, setSubMembers] = useState(null);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersSearch, setMembersSearch] = useState('');
  const [membersPage, setMembersPage] = useState(1);
  const [colorPickerId, setColorPickerId] = useState(null);

  // Sync state
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncLogs, setSyncLogs] = useState([]);
  const [syncRuns, setSyncRuns] = useState([]);
  const [syncRunning, setSyncRunning] = useState(false);
  const [selectedRun, setSelectedRun] = useState('');
  const [logFilter, setLogFilter] = useState('all');

  // Opportunity status state
  const [oppStatuses, setOppStatuses] = useState([]);
  const [newOppStatusName, setNewOppStatusName] = useState('');
  const [editingOppId, setEditingOppId] = useState(null);
  const [editingOppName, setEditingOppName] = useState('');
  const [oppColorPickerId, setOppColorPickerId] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [confRes, availRes, usersRes, subsRes, oppStatusRes] = await Promise.all([
        fetch('/api/admin/salesperson-config'),
        fetch('/api/admin/salesperson-available'),
        fetch('/api/admin/users'),
        fetch('/api/crm/subscription-types-all'),
        fetch('/api/crm/opportunity-statuses'),
      ]);
      if (confRes.ok) setConfig(await confRes.json());
      if (availRes.ok) setAvailable(await availRes.json());
      if (usersRes.ok) setUsers(await usersRes.json());
      if (subsRes.ok) setSubTypes(await subsRes.json());
      if (oppStatusRes.ok) setOppStatuses(await oppStatusRes.json());
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);

  const fetchSyncData = async (runFilter) => {
    try {
      const [statusRes, logsRes] = await Promise.all([
        fetch('/api/admin/sync/status'),
        fetch(`/api/admin/sync/logs${runFilter ? `?run=${runFilter}` : ''}`),
      ]);
      if (statusRes.ok) setSyncStatus(await statusRes.json());
      if (logsRes.ok) {
        const data = await logsRes.json();
        setSyncLogs(data.logs || []);
        setSyncRuns(data.runs || []);
      }
    } catch (err) { console.error(err); }
  };

  useEffect(() => {
    if (tab === 'sync') fetchSyncData(selectedRun || '');
  }, [tab, selectedRun]);

  const triggerSync = async (full) => {
    setSyncRunning(true);
    try {
      await fetch('/api/admin/sync/trigger', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ full }),
      });
      // Poll for completion
      const poll = setInterval(async () => {
        await fetchSyncData('');
        // Check if latest run has a 'complete' or 'error' entry
        const latest = syncRuns[0];
        if (latest?.Result) {
          clearInterval(poll);
          setSyncRunning(false);
        }
      }, 5000);
      // Safety timeout
      setTimeout(() => { clearInterval(poll); setSyncRunning(false); fetchSyncData(''); }, 180000);
    } catch (err) {
      console.error(err);
      setSyncRunning(false);
    }
  };

  const toggleSP = async (person, isActive) => {
    await fetch('/api/admin/salesperson-toggle', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ salesPerson: person, isActive }),
    });
    fetchAll();
  };

  const addPerson = async (person) => {
    await fetch('/api/admin/salesperson-add', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ salesPerson: person }),
    });
    fetchAll();
  };

  const changeRole = async (email, role) => {
    await fetch('/api/admin/user-role', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, role }),
    });
    fetchAll();
  };

  const changeSalesPerson = async (email, salesPerson) => {
    await fetch('/api/admin/user-salesperson', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, salesPerson: salesPerson || null }),
    });
    fetchAll();
  };

  const addUser = async () => {
    const email = newEmail.trim().toLowerCase();
    if (!email || !email.endsWith('@tsllighting.com')) return;
    await fetch('/api/admin/user-add', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, name: newName.trim(), role: newRole }),
    });
    setNewName(''); setNewEmail(''); setNewRole('sales_team');
    fetchAll();
  };

  // Subscription handlers
  const addSubType = async () => {
    if (!newSubName.trim()) return;
    const color = COLOR_PALETTE[subTypes.length % COLOR_PALETTE.length];
    await fetch('/api/crm/subscription-type', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: newSubName.trim(), color }),
    });
    setNewSubName('');
    fetchAll();
  };

  const saveSubName = async (id) => {
    if (!editingName.trim()) return;
    await fetch(`/api/crm/subscription-type/${id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: editingName.trim() }),
    });
    setEditingId(null);
    fetchAll();
  };

  const toggleSubActive = async (id, isActive) => {
    await fetch(`/api/crm/subscription-type/${id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ isActive }),
    });
    fetchAll();
  };

  const viewMembers = async (id, page = 1, search = '') => {
    if (viewingSubId === id && page === 1 && !search) { setViewingSubId(null); setSubMembers(null); return; }
    setViewingSubId(id);
    setMembersPage(page);
    setMembersLoading(true);
    try {
      const params = new URLSearchParams({ page });
      if (search) params.set('q', search);
      const res = await fetch(`/api/crm/subscription-members/${id}?${params}`);
      if (res.ok) setSubMembers(await res.json());
    } catch (e) { console.error(e); }
    setMembersLoading(false);
  };

  const handleMembersSearch = (id, q) => {
    setMembersSearch(q);
    clearTimeout(window._memSearchTimer);
    window._memSearchTimer = setTimeout(() => viewMembers(id, 1, q), 300);
  };

  const changeColor = async (id, color) => {
    await fetch(`/api/crm/subscription-type/${id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ color }),
    });
    setColorPickerId(null);
    fetchAll();
  };

  const roleLabel = (r) => {
    switch(r) {
      case 'admin': return 'Admin';
      case 'director': return 'Director';
      case 'manager': return 'Manager / BDM';
      case 'sales_team': return 'Sales Team';
      default: return 'No Access';
    }
  };
  const roleColor = (r) => {
    switch(r) {
      case 'admin': return '#7c3aed';
      case 'director': return '#38bdf8';
      case 'manager': return '#fb923c';
      case 'sales_team': return '#4ade80';
      default: return '#ef4444';
    }
  };
  const getSubColor = (st) => st.Color || defaultColor(st.Id);

  // Opportunity status handlers
  const addOppStatus = async () => {
    if (!newOppStatusName.trim()) return;
    const color = COLOR_PALETTE[oppStatuses.length % COLOR_PALETTE.length];
    await fetch('/api/crm/opportunity-status', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: newOppStatusName.trim(), color, isClosed: false }),
    });
    setNewOppStatusName('');
    fetchAll();
  };

  const saveOppStatusName = async (id) => {
    if (!editingOppName.trim()) return;
    await fetch(`/api/crm/opportunity-status/${id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: editingOppName.trim() }),
    });
    setEditingOppId(null);
    fetchAll();
  };

  const toggleOppStatusActive = async (id, isActive) => {
    await fetch(`/api/crm/opportunity-status/${id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ isActive }),
    });
    fetchAll();
  };

  const toggleOppStatusClosed = async (id, isClosed) => {
    await fetch(`/api/crm/opportunity-status/${id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ isClosed }),
    });
    fetchAll();
  };

  const changeOppStatusColor = async (id, color) => {
    await fetch(`/api/crm/opportunity-status/${id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ color }),
    });
    setOppColorPickerId(null);
    fetchAll();
  };

  if (loading) return <Loading />;

  return (
    <div className="crm-content">
      <h2 style={{ fontSize: 20, fontWeight: 700, margin: '0 0 4px 0' }}>Admin Settings</h2>
      <p style={{ color: C.textDim, fontSize: 13, marginBottom: 20 }}>
        {isAdmin ? 'Manage users, sales people, subscriptions, and HireTrack sync' : 'Manage subscriptions'}
      </p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {isAdmin && <button className={`admin-tab ${tab === 'users' ? 'active' : ''}`} onClick={() => setTab('users')}>Users</button>}
        {isAdmin && <button className={`admin-tab ${tab === 'salespeople' ? 'active' : ''}`} onClick={() => setTab('salespeople')}>Sales People</button>}
        <button className={`admin-tab ${tab === 'subscriptions' ? 'active' : ''}`} onClick={() => setTab('subscriptions')}>Subscriptions</button>
        <button className={`admin-tab ${tab === 'opp-statuses' ? 'active' : ''}`} onClick={() => setTab('opp-statuses')}>Opportunity Statuses</button>
        {isAdmin && <button className={`admin-tab ${tab === 'sync' ? 'active' : ''}`} onClick={() => setTab('sync')}>HireTrack Sync</button>}
      </div>

      {tab === 'users' && (
        <div className="chart-card">
          <h3 className="chart-title">User Access Management</h3>
          <p style={{ color: C.textDim, fontSize: 12, marginBottom: 16 }}>
            Manage who can access the platform. Users appear here automatically when they sign in with their TSL Google account.
          </p>
          <table className="drilldown-table" style={{ fontSize: 13 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Name</th>
                <th style={{ textAlign: 'left' }}>Email</th>
                <th style={{ textAlign: 'left' }}>Current Role</th>
                <th style={{ textAlign: 'left' }}>Last Login</th>
                <th style={{ textAlign: 'center' }}>Change Role</th>
                <th style={{ textAlign: 'center' }}>Linked Salesperson</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.Email}>
                  <td style={{ textAlign: 'left', fontWeight: 500 }}>{u.DisplayName || '–'}</td>
                  <td style={{ textAlign: 'left' }}>{u.Email}</td>
                  <td style={{ textAlign: 'left' }}>
                    <span style={{ color: roleColor(u.Role), fontWeight: 600 }}>{roleLabel(u.Role)}</span>
                  </td>
                  <td style={{ textAlign: 'left', fontSize: 12, color: '#94a3b8' }}>
                    {u.LastLoginAt ? new Date(u.LastLoginAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Never'}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <select className="crm-filter-select" value={u.Role}
                      onChange={e => changeRole(u.Email, e.target.value)} style={{ fontSize: 12 }}>
                      <option value="admin">Admin</option>
                      <option value="director">Director</option>
                      <option value="manager">Manager / BDM</option>
                      <option value="sales_team">Sales Team</option>
                      <option value="none">No Access</option>
                    </select>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <select className="crm-filter-select" value={u.LinkedSalesPerson || ''}
                      onChange={e => changeSalesPerson(u.Email, e.target.value)} style={{ fontSize: 12 }}>
                      <option value="">— None —</option>
                      {config.map(sp => (
                        <option key={sp.Sales_Person} value={sp.Sales_Person}>{sp.Sales_Person}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="add-user-form">
            <h4 style={{ fontSize: 13, fontWeight: 600, color: C.textMuted, marginBottom: 10 }}>Add New User</h4>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input type="text" placeholder="Display Name" value={newName}
                onChange={e => setNewName(e.target.value)} className="crm-search-input" style={{ width: 160 }} />
              <input type="email" placeholder="email@tsllighting.com" value={newEmail}
                onChange={e => setNewEmail(e.target.value)} className="crm-search-input" style={{ width: 220 }} />
              <select className="crm-filter-select" value={newRole} onChange={e => setNewRole(e.target.value)} style={{ fontSize: 12 }}>
                <option value="admin">Admin</option>
                <option value="director">Director</option>
                <option value="manager">Manager / BDM</option>
                <option value="sales_team">Sales Team</option>
                <option value="none">No Access</option>
              </select>
              <button className="admin-add-btn" onClick={addUser}
                disabled={!newEmail.trim().endsWith('@tsllighting.com')}
                style={{ opacity: newEmail.trim().endsWith('@tsllighting.com') ? 1 : 0.4 }}>
                + Add User
              </button>
            </div>
          </div>
        </div>
      )}

      {tab === 'salespeople' && (
        <>
          <div className="chart-card" style={{ marginBottom: 16 }}>
            <h3 className="chart-title">Active Sales People</h3>
            <p style={{ color: C.textDim, fontSize: 12, marginBottom: 12 }}>
              These people appear in the dropdown filter and the "Confirmed Revenue by Person" chart.
            </p>
            <div className="admin-grid">
              {config.map(p => (
                <div key={p.Sales_Person} className={`admin-person ${p.IsActive ? 'active' : 'inactive'}`}>
                  <span className="admin-person-name">{p.Sales_Person}</span>
                  <button className={`admin-toggle ${p.IsActive ? 'on' : 'off'}`}
                    onClick={() => toggleSP(p.Sales_Person, !p.IsActive)}>
                    {p.IsActive ? 'Active' : 'Inactive'}
                  </button>
                </div>
              ))}
            </div>
          </div>

          {available.length > 0 && (
            <div className="chart-card">
              <h3 className="chart-title">New Sales People (not yet configured)</h3>
              <p style={{ color: C.textDim, fontSize: 12, marginBottom: 12 }}>
                These people exist in the data but haven't been added to the dashboard yet.
              </p>
              <div className="admin-grid">
                {available.map(p => (
                  <div key={p.Sales_Person} className="admin-person new">
                    <span className="admin-person-name">{p.Sales_Person}</span>
                    <button className="admin-add-btn" onClick={() => addPerson(p.Sales_Person)}>+ Add</button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {tab === 'subscriptions' && (
        <>
          <div className="chart-card" style={{ marginBottom: 16 }}>
            <h3 className="chart-title">Subscription Types</h3>
            <p style={{ color: C.textDim, fontSize: 12, marginBottom: 16 }}>
              Manage subscription tags that can be applied to clients and contacts. Deactivated types won't appear in the CRM but existing assignments are preserved.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {subTypes.map(st => (
                <div key={st.Id} style={{ borderBottom: '1px solid #1e2130' }}>
                  <div className="admin-sub-row">
                    <span className="admin-sub-badge" style={{ background: st.IsActive ? getSubColor(st) : '#2a2d3a', cursor: 'pointer', position: 'relative' }}
                      onClick={() => setColorPickerId(colorPickerId === st.Id ? null : st.Id)}>
                      {st.Name.charAt(0).toUpperCase()}
                    </span>
                    {/* Color picker */}
                    {colorPickerId === st.Id && (
                      <div className="admin-color-picker">
                        {COLOR_PALETTE.map(c => (
                          <span key={c} className={`admin-color-swatch ${getSubColor(st) === c ? 'active' : ''}`}
                            style={{ background: c }} onClick={() => changeColor(st.Id, c)} />
                        ))}
                      </div>
                    )}
                    {editingId === st.Id ? (
                      <div style={{ flex: 1, display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input type="text" value={editingName} onChange={e => setEditingName(e.target.value)}
                          className="crm-search-input" style={{ width: 200 }}
                          onKeyDown={e => { if (e.key === 'Enter') saveSubName(st.Id); if (e.key === 'Escape') setEditingId(null); }} />
                        <button className="admin-add-btn" onClick={() => saveSubName(st.Id)}>Save</button>
                        <button className="admin-add-btn" style={{ background: 'transparent', border: '1px solid #2a2d3a', color: C.textMuted }}
                          onClick={() => setEditingId(null)}>Cancel</button>
                      </div>
                    ) : (
                      <>
                        <span style={{ flex: 1, fontWeight: 600, fontSize: 14, color: st.IsActive ? C.text : C.textDim,
                          textDecoration: st.IsActive ? 'none' : 'line-through' }}>
                          {st.Name}
                        </span>
                        <button className="admin-sub-action" onClick={() => { setEditingId(st.Id); setEditingName(st.Name); }}>
                          Edit
                        </button>
                        <button className="admin-sub-action" onClick={() => { setMembersSearch(''); viewMembers(st.Id); }}
                          style={{ color: viewingSubId === st.Id ? C.purple : C.textMuted }}>
                          {viewingSubId === st.Id ? 'Hide' : 'View'} Members
                        </button>
                        <button className={`admin-toggle ${st.IsActive ? 'on' : 'off'}`}
                          onClick={() => toggleSubActive(st.Id, !st.IsActive)} style={{ minWidth: 80 }}>
                          {st.IsActive ? 'Active' : 'Inactive'}
                        </button>
                      </>
                    )}
                  </div>

                  {/* Members panel */}
                  {viewingSubId === st.Id && (
                    <div className="admin-sub-members">
                      <div style={{ marginBottom: 12 }}>
                        <input type="text" placeholder="Search members..." value={membersSearch}
                          onChange={e => handleMembersSearch(st.Id, e.target.value)}
                          className="crm-search-input" style={{ width: '100%' }} />
                      </div>
                      {membersLoading ? (
                        <p style={{ color: C.textDim, fontSize: 13, padding: 12 }}>Loading...</p>
                      ) : subMembers && (
                        <>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                            <div>
                              <h4 style={{ fontSize: 12, fontWeight: 700, color: C.textMuted, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Companies ({subMembers.companyTotal})
                              </h4>
                              {subMembers.companies.length === 0 ? (
                                <p style={{ color: C.textDim, fontSize: 12 }}>No companies tagged</p>
                              ) : (
                                <div className="admin-sub-member-list">
                                  {subMembers.companies.map(c => (
                                    <div key={c.CompanyCounter} className="admin-sub-member-item"
                                      onClick={() => navigate(`/crm/clients/${c.CompanyCounter}`)}>
                                      <span style={{ fontWeight: 600 }}>🏢 {c.CompanyName}</span>
                                      <span style={{ color: C.textDim, fontSize: 11 }}>{c.Town || ''}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div>
                              <h4 style={{ fontSize: 12, fontWeight: 700, color: C.textMuted, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Contacts ({subMembers.contactTotal})
                              </h4>
                              {subMembers.contacts.length === 0 ? (
                                <p style={{ color: C.textDim, fontSize: 12 }}>No contacts tagged</p>
                              ) : (
                                <div className="admin-sub-member-list">
                                  {subMembers.contacts.map(c => (
                                    <div key={c.NameCounter} className="admin-sub-member-item"
                                      onClick={() => navigate(`/crm/contacts/${c.NameCounter}`)}>
                                      <span style={{ fontWeight: 600 }}>👤 {c.FORENAME} {c.SURNAME}</span>
                                      <span style={{ color: C.textDim, fontSize: 11 }}>{c.CompanyName || ''}{c.EMAIL ? ` · ${c.EMAIL}` : ''}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                          {/* Pagination */}
                          {(subMembers.companyTotal > subMembers.pageSize || subMembers.contactTotal > subMembers.pageSize) && (
                            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 12 }}>
                              <button className="admin-sub-action" disabled={subMembers.page <= 1}
                                style={{ opacity: subMembers.page <= 1 ? 0.3 : 1 }}
                                onClick={() => viewMembers(st.Id, subMembers.page - 1, membersSearch)}>← Previous</button>
                              <span style={{ fontSize: 12, color: C.textMuted, padding: '5px 8px' }}>
                                Page {subMembers.page} of {Math.max(
                                  Math.ceil(subMembers.companyTotal / subMembers.pageSize),
                                  Math.ceil(subMembers.contactTotal / subMembers.pageSize)
                                )}
                              </span>
                              <button className="admin-sub-action"
                                disabled={subMembers.companies.length < subMembers.pageSize && subMembers.contacts.length < subMembers.pageSize}
                                style={{ opacity: (subMembers.companies.length < subMembers.pageSize && subMembers.contacts.length < subMembers.pageSize) ? 0.3 : 1 }}
                                onClick={() => viewMembers(st.Id, subMembers.page + 1, membersSearch)}>Next →</button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Add new */}
            <div style={{ marginTop: 20, display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="text" placeholder="New subscription name..." value={newSubName}
                onChange={e => setNewSubName(e.target.value)} className="crm-search-input" style={{ width: 240 }}
                onKeyDown={e => { if (e.key === 'Enter') addSubType(); }} />
              <button className="admin-add-btn" onClick={addSubType}
                disabled={!newSubName.trim()} style={{ opacity: newSubName.trim() ? 1 : 0.4 }}>
                + Add Subscription
              </button>
            </div>
          </div>
        </>
      )}

      {tab === 'opp-statuses' && (
        <>
          <div className="chart-card" style={{ marginBottom: 16 }}>
            <h3 className="chart-title">Opportunity & Lead Statuses</h3>
            <p style={{ color: C.textDim, fontSize: 12, marginBottom: 16 }}>
              Manage the pipeline stages for opportunities and leads. "Closed" statuses mark an opportunity as resolved (Won/Lost). Deactivated statuses won't appear in the pipeline.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {oppStatuses.map(os => (
                <div key={os.Id} style={{ borderBottom: '1px solid #1e2130' }}>
                  <div className="admin-sub-row">
                    <span className="admin-sub-badge"
                      style={{ background: os.IsActive ? (os.Color || '#94a3b8') : '#2a2d3a', cursor: 'pointer', position: 'relative' }}
                      onClick={() => setOppColorPickerId(oppColorPickerId === os.Id ? null : os.Id)}>
                      {os.SortOrder}
                    </span>
                    {oppColorPickerId === os.Id && (
                      <div className="admin-color-picker">
                        {COLOR_PALETTE.map(c => (
                          <span key={c} className={`admin-color-swatch ${os.Color === c ? 'active' : ''}`}
                            style={{ background: c }} onClick={() => changeOppStatusColor(os.Id, c)} />
                        ))}
                      </div>
                    )}
                    {editingOppId === os.Id ? (
                      <div style={{ flex: 1, display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input type="text" value={editingOppName} onChange={e => setEditingOppName(e.target.value)}
                          className="crm-search-input" style={{ width: 200 }}
                          onKeyDown={e => { if (e.key === 'Enter') saveOppStatusName(os.Id); if (e.key === 'Escape') setEditingOppId(null); }} />
                        <button className="admin-add-btn" onClick={() => saveOppStatusName(os.Id)}>Save</button>
                        <button className="admin-add-btn" style={{ background: 'transparent', border: '1px solid #2a2d3a', color: C.textMuted }}
                          onClick={() => setEditingOppId(null)}>Cancel</button>
                      </div>
                    ) : (
                      <>
                        <span style={{ flex: 1, fontWeight: 600, fontSize: 14, color: os.IsActive ? C.text : C.textDim,
                          textDecoration: os.IsActive ? 'none' : 'line-through' }}>
                          {os.Name}
                          {os.IsClosed ? <span style={{ fontSize: 11, color: C.textDim, marginLeft: 8 }}>✓ Closes opportunity</span> : null}
                        </span>
                        <button className="admin-sub-action" onClick={() => { setEditingOppId(os.Id); setEditingOppName(os.Name); }}>
                          Edit
                        </button>
                        <button className={`admin-sub-action`}
                          style={{ color: os.IsClosed ? C.green : C.textDim }}
                          onClick={() => toggleOppStatusClosed(os.Id, !os.IsClosed)}>
                          {os.IsClosed ? 'Closed ✓' : 'Set Closed'}
                        </button>
                        <button className={`admin-toggle ${os.IsActive ? 'on' : 'off'}`}
                          onClick={() => toggleOppStatusActive(os.Id, !os.IsActive)} style={{ minWidth: 80 }}>
                          {os.IsActive ? 'Active' : 'Inactive'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div style={{ marginTop: 20, display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="text" placeholder="New status name..." value={newOppStatusName}
                onChange={e => setNewOppStatusName(e.target.value)} className="crm-search-input" style={{ width: 240 }}
                onKeyDown={e => { if (e.key === 'Enter') addOppStatus(); }} />
              <button className="admin-add-btn" onClick={addOppStatus}
                disabled={!newOppStatusName.trim()} style={{ opacity: newOppStatusName.trim() ? 1 : 0.4 }}>
                + Add Status
              </button>
            </div>
          </div>
        </>
      )}

      {tab === 'sync' && (
        <>
          <div className="chart-card" style={{ marginBottom: 20 }}>
            <h3 className="chart-title">HireTrack NexusDB Sync</h3>
            <p style={{ color: C.textDim, fontSize: 12, marginBottom: 16 }}>
              Sync companies and contacts from HireTrack NexusDB to the CRM. Incremental syncs only update changed records.
            </p>

            {/* Status cards */}
            <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
              <div style={{ background: 'rgba(124,58,237,0.1)', border: '1px solid rgba(124,58,237,0.3)', borderRadius: 8, padding: '12px 20px', minWidth: 180 }}>
                <div style={{ fontSize: 11, color: C.textDim, marginBottom: 4 }}>Companies</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: C.purple }}>{syncStatus?.counts?.companies?.toLocaleString() || '—'}</div>
              </div>
              <div style={{ background: 'rgba(56,189,248,0.1)', border: '1px solid rgba(56,189,248,0.3)', borderRadius: 8, padding: '12px 20px', minWidth: 180 }}>
                <div style={{ fontSize: 11, color: C.textDim, marginBottom: 4 }}>Contacts</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: C.blue }}>{syncStatus?.counts?.contacts?.toLocaleString() || '—'}</div>
              </div>
              {syncStatus?.syncState?.map(s => (
                <div key={s.SyncName} style={{ background: 'rgba(74,222,128,0.1)', border: '1px solid rgba(74,222,128,0.3)', borderRadius: 8, padding: '12px 20px', minWidth: 200 }}>
                  <div style={{ fontSize: 11, color: C.textDim, marginBottom: 4 }}>Last {s.SyncName} sync</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: C.green }}>
                    {new Date(s.LastSyncAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              ))}
            </div>

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
              <button
                className="admin-add-btn"
                onClick={() => triggerSync(false)}
                disabled={syncRunning}
                style={{ opacity: syncRunning ? 0.5 : 1, padding: '8px 20px' }}
              >
                {syncRunning ? '⟲ Syncing...' : '▶ Run Incremental Sync'}
              </button>
              <button
                className="admin-add-btn"
                onClick={() => { if (window.confirm('This will re-sync ALL companies and contacts from NexusDB. Continue?')) triggerSync(true); }}
                disabled={syncRunning}
                style={{ opacity: syncRunning ? 0.5 : 1, padding: '8px 20px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.4)', color: '#ef4444' }}
              >
                {syncRunning ? '⟲ Syncing...' : '⟲ Force Full Sync'}
              </button>
              <button
                className="admin-add-btn"
                onClick={() => fetchSyncData(selectedRun || '')}
                style={{ padding: '8px 16px', background: 'rgba(148,163,184,0.1)', border: '1px solid rgba(148,163,184,0.3)', color: C.textMuted }}
              >
                ↻ Refresh
              </button>
            </div>
            <div style={{ display: 'flex', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
              <button
                className="admin-add-btn"
                onClick={async () => {
                  const r = await fetch('/api/admin/generate-tasks', { method: 'POST' }).then(r => r.json());
                  alert(r.created > 0 ? `Generated ${r.created} new tasks (${r.contactTasks || 0} contact, ${r.recurringTasks || 0} recurring)` : 'No new tasks to generate — all up to date');
                }}
                style={{ padding: '8px 20px', background: 'rgba(124,58,237,0.15)', border: '1px solid rgba(124,58,237,0.4)', color: '#a78bfa' }}
              >
                ☑ Generate Auto Tasks
              </button>
              <button
                className="admin-add-btn"
                onClick={async () => {
                  if (!window.confirm('Send daily digest email to all CRM users now?')) return;
                  const r = await fetch('/api/admin/send-digest', { method: 'POST' }).then(r => r.json());
                  alert(r.sent > 0 ? `Digest sent to ${r.sent} user${r.sent > 1 ? 's' : ''}` : 'No digests to send — all users up to date');
                }}
                style={{ padding: '8px 20px', background: 'rgba(74,222,128,0.15)', border: '1px solid rgba(74,222,128,0.4)', color: '#4ade80' }}
              >
                📧 Send Daily Digest
              </button>
              <button
                className="admin-add-btn"
                onClick={async () => {
                  const email = window.prompt('Enter user email to seed test data for:');
                  if (!email) return;
                  const name = window.prompt('Display name:', email.split('@')[0]);
                  if (!window.confirm(`Seed test data for ${email}?\n\nThis will assign clients, create opportunities, tasks, notifications, and activity.`)) return;
                  const r = await fetch('/api/admin/seed-user-data', {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email, displayName: name }),
                  }).then(r => r.json());
                  if (r.ok) {
                    const s = r.summary;
                    alert(`Done! Seeded for ${email}:\n• ${s.clientsAssigned} clients assigned\n• ${s.opportunitiesCreated} opportunities\n• ${s.tasksCreated} tasks\n• ${s.notificationsCreated} notifications\n• ${s.activitiesLogged} activities logged\n• ${s.contactFrequenciesSet} contact frequencies\n• ${s.ratingsSet} star ratings`);
                  } else {
                    alert('Error: ' + (r.error || 'Unknown error'));
                  }
                }}
                style={{ padding: '8px 20px', background: 'rgba(251,146,60,0.15)', border: '1px solid rgba(251,146,60,0.4)', color: '#fb923c' }}
              >
                🧪 Seed Test Data
              </button>
            </div>
            {syncRunning && (
              <p style={{ color: C.orange, fontSize: 12, marginTop: 4 }}>
                Sync is running in the background. This page will auto-refresh when complete.
              </p>
            )}
          </div>

          {/* Sync Log */}
          <div className="chart-card">
            <h3 className="chart-title">Sync Log</h3>

            {/* Filters */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
              <select
                value={selectedRun}
                onChange={e => setSelectedRun(e.target.value)}
                className="crm-search-input"
                style={{ width: 340, fontSize: 12 }}
              >
                <option value="">All runs</option>
                {syncRuns.map(r => (
                  <option key={r.RunId} value={r.RunId}>
                    {new Date(r.StartedAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    {' — '}{r.SyncType || 'Sync'} — {r.EntryCount} entries
                    {r.Result ? ` — ${r.Result}` : ''}
                  </option>
                ))}
              </select>

              <div style={{ display: 'flex', gap: 4 }}>
                {['all', 'insert', 'update', 'delete', 'push', 'error'].map(f => (
                  <button
                    key={f}
                    onClick={() => setLogFilter(f)}
                    style={{
                      padding: '4px 10px', fontSize: 11, borderRadius: 4, cursor: 'pointer',
                      background: logFilter === f ? 'rgba(124,58,237,0.2)' : 'rgba(148,163,184,0.1)',
                      border: `1px solid ${logFilter === f ? 'rgba(124,58,237,0.5)' : 'rgba(148,163,184,0.2)'}`,
                      color: logFilter === f ? C.purpleLight : C.textDim,
                    }}
                  >
                    {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Log table */}
            <div style={{ maxHeight: 500, overflowY: 'auto', borderRadius: 8 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(148,163,184,0.2)', position: 'sticky', top: 0, background: '#1a1f2e', zIndex: 1 }}>
                    <th style={{ textAlign: 'left', padding: '8px 12px', color: C.textDim, fontWeight: 600 }}>Time</th>
                    <th style={{ textAlign: 'left', padding: '8px 12px', color: C.textDim, fontWeight: 600 }}>Type</th>
                    <th style={{ textAlign: 'left', padding: '8px 12px', color: C.textDim, fontWeight: 600 }}>Action</th>
                    <th style={{ textAlign: 'left', padding: '8px 12px', color: C.textDim, fontWeight: 600 }}>Name</th>
                    <th style={{ textAlign: 'left', padding: '8px 12px', color: C.textDim, fontWeight: 600 }}>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {syncLogs
                    .filter(l => logFilter === 'all' || l.Action === logFilter)
                    .map((l, i) => {
                      const actionColors = {
                        insert: C.green, update: C.blue, delete: C.red, push: C.purple,
                        error: C.red, start: C.orange, complete: C.green, summary: C.yellow,
                        fetch: C.textDim, refresh: C.textDim,
                      };
                      return (
                        <tr key={l.Id || i} style={{ borderBottom: '1px solid rgba(148,163,184,0.08)' }}>
                          <td style={{ padding: '6px 12px', color: C.textDim, whiteSpace: 'nowrap' }}>
                            {new Date(l.CreatedAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                          </td>
                          <td style={{ padding: '6px 12px', color: C.text }}>
                            <span style={{
                              display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                              background: l.SyncType === 'company' ? 'rgba(124,58,237,0.15)' : l.SyncType === 'contact' ? 'rgba(56,189,248,0.15)' : l.SyncType === 'staged' ? 'rgba(251,146,60,0.15)' : 'rgba(148,163,184,0.15)',
                              color: l.SyncType === 'company' ? C.purpleLight : l.SyncType === 'contact' ? C.blue : l.SyncType === 'staged' ? C.orange : C.textMuted,
                            }}>
                              {l.SyncType}
                            </span>
                          </td>
                          <td style={{ padding: '6px 12px' }}>
                            <span style={{ color: actionColors[l.Action] || C.text, fontWeight: 600 }}>
                              {l.Action}
                            </span>
                          </td>
                          <td style={{ padding: '6px 12px', color: C.text, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {l.EntityName || '—'}
                          </td>
                          <td style={{ padding: '6px 12px', color: C.textDim, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {l.Details || '—'}
                          </td>
                        </tr>
                      );
                    })}
                  {syncLogs.filter(l => logFilter === 'all' || l.Action === logFilter).length === 0 && (
                    <tr><td colSpan={5} style={{ padding: 20, textAlign: 'center', color: C.textDim }}>
                      {syncLogs.length === 0 ? 'No sync logs yet. Run a sync to get started.' : 'No entries match this filter.'}
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
